import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, MenuItem, Select, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Typography, Tooltip, Button, Paper, InputBase, IconButton, Divider, TableContainer } from "@mui/material";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFileExcel, faHistory, faFilter, faCalendarAlt, faSyncAlt, faUserShield, faUserAlt } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import TablePaginationActions from '../../../../base-components/TablePaginationActions';
import {
    EXCEL_DATETIME_FORMAT,
    finalizeWorksheetDates,
    toExcelSerialDateTime,
} from '../../../../utils/reportExcelExport';
import { isDynamicStaffRole } from '../../../../utils/roleAccess';

const BASE_URL = process.env.REACT_APP_BASE_URL;

function MemberStatusAudit() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [logs, setLogs] = useState([]);
    const [totalLogs, setTotalLogs] = useState(0);
    const [role, setRole] = useState('');
    const [location_id, setLocationID] = useState('');
    const [company_id, setCompanyID] = useState('');

    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        const sessionStr = sessionStorage.getItem('user-info');
        if (sessionStr) {
            const session = JSON.parse(sessionStr);
            setRole(session.user.role);
            setCompanyID(session.user.company_id);
            setLocationID(session.user.location_id);
        } else {
            navigate('/login');
        }
    }, [navigate]);

    const fetchLogs = useCallback(async (
        currentPage = page, 
        currentLimit = rowsPerPage, 
        currentSearch = searchQuery, 
        currentStatus = statusFilter, 
        currentFrom = fromDate, 
        currentTo = toDate
    ) => {
        setLoading(true);
        try {
            let params = new URLSearchParams();
            if (role === 'Admin') params.append('company_id', company_id);
            if (role === 'ClubAdmin') params.append('location_id', location_id);
            else if (isDynamicStaffRole(role)) params.append('location_id', location_id);
            
            params.append('page', currentPage + 1);
            params.append('limit', currentLimit);
            
            if (currentSearch) params.append('search', currentSearch);
            if (currentStatus !== "All") params.append('status', currentStatus);
            if (currentFrom) params.append('from_date', currentFrom);
            if (currentTo) params.append('to_date', currentTo);

            const response = await fetch(`${BASE_URL}members/get-status-logs?${params.toString()}`);
            const data = await response.json();
            if (response.ok) {
                setLogs(data.logs);
                setTotalLogs(data.totalLogs);
            }
        } catch (error) {
            console.error("Error fetching status logs:", error);
        } finally {
            setLoading(false);
        }
    }, [role, company_id, location_id, page, rowsPerPage, searchQuery, statusFilter, fromDate, toDate]);

    useEffect(() => {
        if (role) {
            fetchLogs();
        }
    }, [role, page, rowsPerPage]);

    const handleFilter = () => {
        setPage(0);
        fetchLogs(0, rowsPerPage);
    };

    const handleClear = () => {
        setFromDate("");
        setToDate("");
        setStatusFilter("All");
        setSearchQuery("");
        setPage(0);
        fetchLogs(0, rowsPerPage, "", "All", "", "");
    };

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            let params = new URLSearchParams();
            if (role === 'Admin') params.append('company_id', company_id);
            if (role === 'ClubAdmin') params.append('location_id', location_id);
            else if (isDynamicStaffRole(role)) params.append('location_id', location_id);
            params.append('all', 'true');
            if (searchQuery) params.append('search', searchQuery);
            if (statusFilter !== "All") params.append('status', statusFilter);
            if (fromDate) params.append('from_date', fromDate);
            if (toDate) params.append('to_date', toDate);

            const response = await fetch(`${BASE_URL}members/get-status-logs?${params.toString()}`);
            const data = await response.json();
            
            if (!response.ok) throw new Error("Export data fetch failed");

            const exportData = data.logs.map(log => ({
                'Date & Time': toExcelSerialDateTime(log.createdAt) ?? '',
                'Member ID': log.member_id?.member_id || 'N/A',
                'Member Name': log.member_id?.name || 'N/A',
                'Action': log.action_type,
                'Previous Status': log.previous_status ? 'Active' : 'Inactive',
                'New Status': log.new_status ? 'Active' : 'Inactive',
                'Performed By': log.changed_by?.name || 'System',
                'Remarks': log.remarks || ''
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            finalizeWorksheetDates(worksheet, {
                'Date & Time': EXCEL_DATETIME_FORMAT,
            });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Member Status Audit");

            const wscols = [
                { wch: 20 }, { wch: 12 }, { wch: 25 }, { wch: 15 },
                { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 50 },
            ];
            worksheet['!cols'] = wscols;

            XLSX.writeFile(workbook, `Member_Audit_Log_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setExporting(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <div className="p-4 md:p-8 bg-gx-canvas min-h-screen">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                    <Box>
                        <Typography variant="h4" className="font-extrabold text-gx-navy flex items-center gap-3">
                            <div className="bg-gx-navy p-3 rounded-gx shadow-gx">
                                <FontAwesomeIcon icon={faHistory} className="text-white text-2xl" />
                            </div>
                            Member Status Audit
                        </Typography>
                        <Typography variant="body2" className="text-gx-ink-soft mt-1 ml-1">
                            Track and monitor all member reactivation and deactivation events.
                        </Typography>
                    </Box>
                    
                    <div className="flex gap-3">
                        <Button 
                            variant="outlined" 
                            startIcon={<FontAwesomeIcon icon={faSyncAlt} />}
                            onClick={() => fetchLogs()}
                            className="border-gx-navy text-gx-navy hover:bg-gx-navy/5 px-4 py-2 rounded-xl capitalize font-bold h-[45px]"
                        >
                            Refresh
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FontAwesomeIcon icon={faFileExcel} />}
                            onClick={handleExportExcel}
                            disabled={totalLogs === 0 || exporting}
                            className="bg-[#107c41] hover:bg-[#0e6b38] text-white px-6 py-2 rounded-xl capitalize font-bold shadow-md h-[45px]"
                        >
                            {exporting ? 'Exporting...' : 'Export Excel'}
                        </Button>
                    </div>
                </div>

                {/* Filters Section */}
                <Paper elevation={0} className="p-6 rounded-2xl border border-gx-border mb-8 bg-white shadow-gx">
                    <div className="flex items-center gap-2 mb-6 border-b border-gx-border pb-4">
                        <FontAwesomeIcon icon={faFilter} className="text-gx-red" />
                        <Typography className="font-bold text-gx-ink">Filter Controls</Typography>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gx-ink-soft flex items-center gap-2">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-[10px]" />
                                From Date
                            </label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="w-full border border-gx-border rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-[#D71920]/20 focus:border-gx-red transition-all bg-gx-canvas/50"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gx-ink-soft flex items-center gap-2">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-[10px]" />
                                To Date
                            </label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                min={fromDate}
                                className="w-full border border-gx-border rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-[#D71920]/20 focus:border-gx-red transition-all bg-gx-canvas/50"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gx-ink-soft flex items-center gap-2">
                                <FontAwesomeIcon icon={faSyncAlt} className="text-[10px]" />
                                Audit Type
                            </label>
                            <Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full h-[46px] bg-gx-canvas/50 border-gx-border rounded-xl text-sm"
                                size="small"
                                displayEmpty
                            >
                                <MenuItem value="All">All Statuses</MenuItem>
                                <MenuItem value="Active">Active (Restored)</MenuItem>
                                <MenuItem value="Inactive">Inactive (Deactivated)</MenuItem>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gx-ink-soft flex items-center gap-2">
                                <FontAwesomeIcon icon={faSearch} className="text-[10px]" />
                                Search Member
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search by name or ID..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleFilter()}
                                    className="w-full border border-gx-border rounded-xl p-3 pl-11 text-sm outline-none focus:ring-2 focus:ring-[#D71920]/20 focus:border-gx-red transition-all bg-gx-canvas/50"
                                />
                                <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-[14px] text-gx-ink-soft" />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8 pt-6 border-t border-gx-border">
                        <Button 
                            variant="text" 
                            onClick={handleClear}
                            className="text-gx-ink-soft hover:text-gx-ink-soft font-bold px-6 order-2 sm:order-1"
                        >
                            Reset Defaults
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleFilter}
                            className="bg-gx-red hover:bg-[#b5161b] text-white px-10 py-2.5 rounded-xl font-bold shadow-lg shadow-[#D71920]/20 transition-all hover:-translate-y-0.5 order-1 sm:order-2"
                        >
                            Apply Filters
                        </Button>
                    </div>
                </Paper>

                {/* Table Section */}
                {loading ? (
                    <div className="flex flex-col justify-center items-center py-32 gap-4">
                        <CircularProgress color="secondary" size={50} />
                        <Typography className="text-gx-ink-soft font-medium animate-pulse text-sm">Synchronizing audit data...</Typography>
                    </div>
                ) : (
                    <Paper elevation={0} className="rounded-2xl border border-gx-border bg-white shadow-gx transition-all hover:shadow-md">
                        <TableContainer className="rounded-2xl overflow-x-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                            <Table sx={{ minWidth: 900 }}>
                                <TableHead className="bg-[#fcfdfe]">
                                    <TableRow>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest pl-8 whitespace-nowrap">Timestamp</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap">Member Details</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap">Action Event</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap">Transition</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest whitespace-nowrap">Performed By</TableCell>
                                        <TableCell className="font-bold text-gx-ink-soft text-[11px] uppercase tracking-widest pr-8 whitespace-nowrap">Audit Remarks</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.length > 0 ? (
                                        logs.map((log) => (
                                            <TableRow key={log._id} hover className="transition-colors">
                                                <TableCell className="pl-8 py-5 border-b border-gx-border whitespace-nowrap">
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-gx-navy text-sm italic">{dayjs(log.createdAt).format('DD MMM, YYYY')}</span>
                                                        <span className="text-gx-ink-soft text-[11px] font-medium uppercase tracking-tighter">{dayjs(log.createdAt).format('hh:mm A')}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap">
                                                    <div className="flex items-center gap-3">
                                                        <div className="bg-gx-navy/5 p-2 rounded-lg text-gx-navy">
                                                            <FontAwesomeIcon icon={faUserAlt} size="xs" />
                                                        </div>
                                                        <div>
                                                            <Typography variant="body2" className="font-bold text-gx-navy">
                                                                {log.member_id?.name || 'Unknown Member'}
                                                            </Typography>
                                                            <span className="text-[10px] font-bold text-gx-ink-soft bg-gx-canvas px-2 py-0.5 rounded border border-gx-border">
                                                                ID: {log.member_id?.member_id || 'N/A'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap">
                                                    <span className={`px-3 py-1.5 rounded-lg text-[10px] font-extrabold uppercase tracking-widest shadow-gx ${
                                                        log.action_type.toLowerCase().includes('reactivation') || log.action_type.toLowerCase().includes('restore')
                                                            ? 'bg-green-50 text-green-600 border border-green-100'
                                                            : 'bg-rose-50 text-rose-600 border border-rose-100'
                                                    }`}>
                                                        {log.action_type}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-2 h-2 rounded-full ${log.previous_status ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 'bg-gray-300'}`}></div>
                                                        <FontAwesomeIcon icon={faSyncAlt} className="text-[10px] text-gx-ink-soft" />
                                                        <div className={`w-2 h-2 rounded-full ${log.new_status ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.4)]'}`}></div>
                                                    </div>
                                                    <Typography className="text-[10px] text-gx-ink-soft uppercase font-bold tracking-tighter mt-1">
                                                        {log.previous_status ? 'Active' : 'Inactive'} → {log.new_status ? 'Active' : 'Inactive'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        {log.changed_by ? (
                                                            <div className="flex items-center gap-2">
                                                                <div className="bg-gx-red/5 p-1.5 rounded text-gx-red">
                                                                    <FontAwesomeIcon icon={faUserShield} size="xs" />
                                                                </div>
                                                                <span className="text-sm font-semibold text-gx-ink">{log.changed_by?.name}</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-[11px] font-black text-gx-ink-soft italic uppercase">System Task</span>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="py-5 border-b border-gx-border pr-8 min-w-[300px]">
                                                    <Typography className="text-xs text-gx-ink-soft italic font-medium leading-relaxed">
                                                        {log.remarks || 'No detailed log available'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" className="py-24">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className="bg-gx-canvas p-6 rounded-full text-gx-ink-soft">
                                                        <FontAwesomeIcon icon={faHistory} size="3x" />
                                                    </div>
                                                    <Typography className="text-gx-ink-soft font-bold tracking-tight">No audit trails found matching your current filters.</Typography>
                                                    <Button variant="text" size="small" onClick={handleClear} className="text-gx-red font-bold">Clear All Search Terms</Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TablePagination
                                            rowsPerPageOptions={[10, 25, 50]}
                                            colSpan={6}
                                            count={totalLogs}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            ActionsComponent={TablePaginationActions}
                                            className="border-none"
                                        />
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
            </div>
            <style jsx>{`
                .MuiTablePagination-root {
                    color: #64748b;
                    font-weight: 600;
                }
                .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows {
                    text-transform: uppercase;
                    font-size: 11px;
                    letter-spacing: 0.05em;
                }
            `}</style>
        </>
    );
}

export default MemberStatusAudit;
